<template>
  <div class="location-profile">
    <v-skeleton-loader v-if="profileLoading" type="card-heading, list-item-three-line, article, article" />

    <v-alert v-else-if="profileError" type="error" text class="rounded-xl" role="alert">
      <div class="d-flex flex-wrap align-center justify-space-between">
        <span>{{ profileError }}</span>
        <v-btn text color="error" min-height="44" @click="loadLocation">Try again</v-btn>
      </div>
    </v-alert>

    <template v-else-if="location">
      <v-card outlined class="admin-panel rounded-xl mb-5 location-profile__hero">
        <v-card-text class="pa-5 pa-md-6">
          <div class="location-profile__identity">
            <v-avatar color="primary" size="72" class="location-profile__logo">
              <v-img v-if="location.logoURL && !logoBroken" :src="location.logoURL" :alt="`${location.name} logo`"
                contain @error="logoBroken = true" />
              <v-icon v-else size="38" dark aria-hidden="true">mdi-store-marker-outline</v-icon>
            </v-avatar>
            <div class="location-profile__identity-copy">
              <div class="d-flex flex-wrap align-center mb-1">
                <h1 class="text-h4 font-weight-bold mr-3">{{ location.name }}</h1>
                <v-chip small outlined :color="location.isActive ? 'success' : 'error'">
                  {{ location.isActive ? 'Active' : 'Inactive' }}
                </v-chip>
              </div>
              <div v-if="location.dba" class="text-subtitle-1 admin-muted">Doing business as {{ location.dba }}</div>
              <div class="text-caption admin-muted">{{ organizationName }}</div>
            </div>
            <v-btn color="primary" depressed rounded min-height="44" :href="terminalHref" :disabled="!terminalHref"
              target="_blank" rel="noopener" :aria-label="`Open ${location.name} register`">
              <v-icon left>mdi-open-in-new</v-icon>Open register
            </v-btn>
          </div>

          <v-row class="mt-5" dense>
            <v-col cols="12" sm="6" lg="3">
              <div class="profile-fact"><span>Register code</span><code>{{ location.code || 'Not assigned' }}</code></div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <div class="profile-fact"><span>Business type</span><strong class="text-capitalize">{{ location.businessType || location.type || 'Not provided' }}</strong></div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <div class="profile-fact"><span>Time zone</span><strong>{{ location.timeZone || 'Not provided' }}</strong></div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <div class="profile-fact"><span>Brand</span><strong class="text-capitalize">{{ location.brand || currentBrand || 'Not provided' }}</strong></div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-row align="start">
        <v-col cols="12" lg="7">
          <ProfileSection icon="mdi-map-marker-outline" title="Contact & address" subtitle="Store contact and location details">
            <v-row>
              <v-col cols="12" md="7">
                <div class="profile-detail-row"><v-icon>mdi-map-marker-outline</v-icon><div><span>Address</span><strong>{{ addressText || 'No address provided' }}</strong></div></div>
              </v-col>
              <v-col cols="12" md="5">
                <div class="profile-detail-row"><v-icon>mdi-phone-outline</v-icon><div><span>Phone</span><strong>{{ address.phoneNumber || 'Not provided' }}</strong></div></div>
                <div class="profile-detail-row"><v-icon>mdi-email-outline</v-icon><div><span>Email</span><strong>{{ location.email || 'Not provided' }}</strong></div></div>
                <div class="profile-detail-row"><v-icon>mdi-web</v-icon><div><span>Website</span><strong>{{ location.website || 'Not provided' }}</strong></div></div>
              </v-col>
            </v-row>
          </ProfileSection>
        </v-col>
        <v-col cols="12" lg="5">
          <ProfileSection icon="mdi-calendar-range" title="Performance period" subtitle="Choose the range used by sales and reports">
            <DateTimePicker :defaultRange="dateTime.range" :range="true" v-model="dateTime"
              :initStartDate="start" :initEndDate="end" @submit="getLocationSales" />
            <div class="date-range-summary mt-3"><span>{{ dateTime.start | moment('LLL') }}</span><v-icon small>mdi-arrow-right</v-icon><span>{{ dateTime.end | moment('LLL') }}</span></div>
          </ProfileSection>
        </v-col>
      </v-row>

      <ProfileSection icon="mdi-chart-line" title="Performance overview" subtitle="Sales, payments, profit, tax, and activity for the selected period">
        <SaleBaseTile ref="saleBaseTile" :dateTime="dateTime" :id="location.id" />
      </ProfileSection>

      <ProfileSection icon="mdi-file-chart-outline" title="Reports" subtitle="Open detailed reports for this location and selected period">
        <Reports :LocationId="location.id" :location="location" :Time="dateTime" />
      </ProfileSection>

      <ProfileSection icon="mdi-credit-card-outline" title="Subscription" subtitle="Plan status, billing period, and upcoming invoice">
        <Subscription :location="location" hideManagementActions />
      </ProfileSection>

      <ProfileSection icon="mdi-account-group-outline" title="Employees" subtitle="People who can access this location">
        <div class="employee-management-cta">
          <v-avatar color="primary" size="48"><v-icon dark>mdi-account-cog-outline</v-icon></v-avatar>
          <div class="employee-management-cta__copy">
            <div class="text-subtitle-1 font-weight-bold">Manage employee access</div>
            <p class="admin-muted mb-0">Add employees and manage their role and access for this location from the employee directory.</p>
          </div>
          <v-btn color="primary" depressed rounded min-height="44" :to="{ name: 'employee-list' }">
            Manage employees<v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </ProfileSection>

      <ProfileSection icon="mdi-cog-outline" title="Operational settings" subtitle="Register behavior, shifts, notifications, and data controls">
        <Settings :LocationId="location.id" :location="location" @updated="notificationsKey += 1" />
      </ProfileSection>

      <ProfileSection icon="mdi-bell-outline" title="Notifications" subtitle="Configure sales and operational alerts">
        <Notifications :key="notificationsKey" :LocationId="location.id" :location="location" />
      </ProfileSection>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import locationService from "@/modules/Location/service.js";
import DateTimePicker from "@/components/DateTimePicker";
import SaleBaseTile from "@/modules/Location/components/SaleBase.vue";
import Settings from "../components/Settings";
import Reports from "../components/Reports";
import Notifications from "../components/Notifications";
import Subscription from "@/modules/Location/components/Subscription.vue";

const ProfileSection = {
  functional: true,
  props: ["icon", "title", "subtitle"],
  render(h, context) {
    return h("v-card", { staticClass: "admin-panel rounded-xl mb-5", props: { outlined: true } }, [
      h("v-card-title", { staticClass: "location-profile-section__header" }, [
        h("v-icon", { staticClass: "mr-3", props: { color: "primary" } }, context.props.icon),
        h("div", [h("div", { staticClass: "text-subtitle-1 font-weight-bold" }, context.props.title), h("div", { staticClass: "text-caption admin-muted font-weight-regular" }, context.props.subtitle)]),
      ]),
      h("v-divider"),
      h("v-card-text", { staticClass: "pa-4 pa-sm-5" }, context.slots().default),
    ]);
  },
};

export default {
  name: "location-view-profile",
  components: { ProfileSection, Settings, Reports, Subscription, SaleBaseTile, Notifications, DateTimePicker },
  data() {
    return {
      start: this.$moment().startOf("day"),
      end: this.$moment().endOf("day"),
      dateTime: { range: "thisMonth", start: this.$moment().startOf("day").toISOString(), end: this.$moment().endOf("day").toISOString() },
      location: null,
      profileLoading: true,
      profileError: "",
      logoBroken: false,
      notificationsKey: 0,
      locationIntegration: false,
    };
  },
  created() { this.loadLocation(); },
  computed: {
    ...mapGetters("brand", ["brandConfig", "currentBrand"]),
    ...mapGetters("global", ["currentOrganization"]),
    address() { return this.location.Address || {}; },
    organizationName() { return (this.location.Organization && this.location.Organization.name) || (this.currentOrganization && this.currentOrganization.name) || "Organization"; },
    addressText() {
      const state = this.address.State && (this.address.State.name || this.address.State.code);
      return [this.address.addressLine1, this.address.addressLine2, this.address.city, state, this.address.postalCode].filter(Boolean).join(", ");
    },
    terminalHref() {
      const base = this.brandConfig && this.brandConfig.urls && this.brandConfig.urls.terminal;
      return base && this.location.code ? `${base}/login/location?code=${encodeURIComponent(this.location.code)}` : null;
    },
  },
  methods: {
    async loadLocation() {
      if (!this.$route.params || !this.$route.params.id) return this.$router.push("/location");
      this.profileLoading = true;
      this.profileError = "";
      this.$Progress.start();
      try {
        const response = await locationService.getbyId(this.$route.params.id, { includeOrganization: true });
        this.location = response.data;
        this.logoBroken = false;
        this.$Progress.finish();
        this.getTerminalExpense();
      } catch (error) {
        this.location = null;
        this.profileError = "This location could not be loaded. Please try again.";
        this.$Progress.fail();
      } finally {
        this.profileLoading = false;
      }
    },
    getLocationSales() {
      const tile = this.$refs.saleBaseTile;
      return tile && typeof tile.loadData === "function" ? tile.loadData() : Promise.resolve();
    },
    async getTerminalExpense() {
      try {
        const response = await locationService.getIntegrationByLocation(this.location.id, { module: "terminalExpense" });
        this.locationIntegration = response.data[0] || false;
      } catch (error) {
        this.locationIntegration = false;
      }
    },
  },
};
</script>

<style scoped>
.location-profile { max-width: 1440px; margin: 0 auto; }
.location-profile__identity { align-items: center; display: flex; gap: 18px; }
.location-profile__logo { flex: 0 0 auto; overflow: hidden; }
.location-profile__logo .v-image { background: white; }
.location-profile__identity-copy { flex: 1; min-width: 0; }
.profile-fact { background: var(--brand-bg); border-radius: 12px; display: flex; flex-direction: column; min-height: 72px; padding: 12px 14px; }
.profile-fact span, .profile-detail-row span { color: rgba(0, 0, 0, .6); font-size: .75rem; font-weight: 600; }
.profile-fact code { background: transparent; color: inherit; font-size: .95rem; font-weight: 700; overflow-wrap: anywhere; }
.location-profile-section__header { min-height: 68px; padding: 15px 20px; }
.profile-detail-row { align-items: flex-start; display: flex; gap: 12px; min-height: 58px; padding: 7px 0; }
.profile-detail-row > div { display: flex; flex-direction: column; min-width: 0; }
.profile-detail-row strong { overflow-wrap: anywhere; }
.date-range-summary { align-items: center; display: flex; flex-wrap: wrap; gap: 8px; font-size: .8125rem; }
.employee-management-cta { align-items: center; display: flex; gap: 16px; }
.employee-management-cta__copy { flex: 1; min-width: 0; }
@media (max-width: 599px) {
  .location-profile__identity { align-items: flex-start; flex-wrap: wrap; }
  .location-profile__identity-copy { flex-basis: calc(100% - 90px); }
  .location-profile__identity h1 { font-size: 1.5rem !important; overflow-wrap: anywhere; }
  .location-profile__identity > .v-btn { width: 100%; }
  .location-profile-section__header { align-items: flex-start; padding: 14px 16px; }
  .employee-management-cta { align-items: flex-start; flex-wrap: wrap; }
  .employee-management-cta__copy { flex-basis: calc(100% - 64px); }
  .employee-management-cta > .v-btn { width: 100%; }
}
</style>
